/**
 * `RenderCursor` — the **public render-skip API** (row-identity ledger, slice
 * **L5 promotion**). Where `RenderSkip` (`./ledger-render-skip`) is the pure LR6
 * decision/record/referee CORE, this module is the *choreography* a paint
 * surface consumes: a consumer's whole obligation is **"ask before painting,
 * mark after painting"** — eviction, structural safety, and per-surface record
 * isolation are owned here by the SDK, not hand-rolled inside each binder.
 *
 * ## The registry + cursor split
 *
 * A `RenderCursors` registry is minted per sheet (one per `SourceRowCellData`,
 * reached via `stores.sourceCellData.renderCursors`). It hands out one
 * `RenderCursor` per **paint surface** (`acquire(surfaceId, …)`): the DOM binder
 * takes one per instance (`RenderCursors.uniqueSurfaceId("dom-default")` — two
 * binders sharing an id would cross-skip each other's slots, since records are
 * per-slot truth), the CLI TUI leases a stable `"cli-tui"` for its `--watch`
 * loop and releases it on shutdown (its reuse also requires its own
 * carried-cache hit, so a stable id is safe there).
 *
 * ## Surfaces are leased, not kept
 *
 * Rows can be pruned (`reconcile`); surfaces used not to be, so a registry held
 * every cursor that ever painted it — and a binder that switches sheets, or an
 * element torn down over a still-cached sheet, left its records behind forever
 * `acquire(surfaceId, …)` returns a
 * `RenderSurfaceLease`: the registry refcounts leases per id, and the LAST
 * `release()` deletes the surface with every record on it. `snapshot().surfaces`
 * is therefore a real liveness measure — it returns to baseline once the holders
 * let go.
 *
 * Each `RenderCursor` **composes** a private per-surface `RenderSkip` (Q1 — the
 * L5 file + its tests stay the one owner of decide/record/referee semantics) and
 * adds only a `paintedEpoch: Map<RowLocalId, number>` on top. The raw L5 hooks
 * (`decide`/`shouldSkip`/`refereeAfterBind`) delegate **1:1** so the DOM dark
 * fingerprint stream stays byte-identical when the binder rewires onto the
 * cursor; the higher-level `shouldRebind` folds in the structural-epoch gate the
 * version compare alone cannot see.
 *
 * ## The structural epoch — LI6, the version-blind bindings change
 *
 * LR6 keys on a row's OUTPUT version node, which is bumped only by writes to the
 * row's own cells/attrs. It cannot see a *bindings* change with zero version
 * movement: a reparent/delete/reorder shifts a row's position, and an inserted
 * renderer-child materializes new fixed-child bindings, all without moving any
 * recorded output version. The SDK closes that gap with a monotonic structural
 * epoch: `reconcile` (wired on `SheetStructureChanged`) bumps it and drops every
 * cursor's records for rows no longer live (the id-less coalesced event carries
 * no removal id, so the SDK reconciles records against the live row set — the
 * `onReexpand` precedent); `invalidateStructure` (wired on `NewRowInserted`) is
 * an O(1) epoch-only bump. Both wirings are scoped by the event's `sheetId` —
 * this registry belongs to ONE sheet, and a foreign sheet's structural event
 * must not advance it. `shouldRebind` forces a rebind whenever a row's
 * `paintedEpoch` lags the current epoch, so every position/child-set-dependent
 * output re-paints exactly once per structural change.
 *
 * The epoch gate lives ONLY in `shouldRebind` and is checked BEFORE
 * `shouldSkip`, so an epoch-forced rebind never counts as an authorized `skip`.
 *
 * ## Dark-first, same as L5
 *
 * `acquire(id, {mode})` passes `mode` to the composed `RenderSkip` (`RenderSkip.Mode`).
 * A dark cursor always returns `true` from `shouldRebind` (the bind proceeds),
 * yet the decision is still computed + counted + refereed — exactly the L5 dark
 * contract. The DOM mints `mode: "follow-flags"` (follow the sheet's
 * `registries.render.live()`); the CLI mints `mode: "live"` and consumes the skip for real, refereed
 * by scene-equality.
 *
 * ## The lease witness
 *
 * The headless suite pins the lifetime contract, but nothing proved that the
 * DOM consumer releases exactly once on a real sheet switch — a binder that
 * re-acquires without releasing leaves the surface it walked away from
 * registered, which is the leak in its live form. `acquire` therefore checks a
 * pure invariant (`leaseViolation`) and emits `witness/render-cursor-leak` when
 * it trips; a `render-cursor/lease` census rides every release so a trace can
 * read the surface count even when nothing is wrong. See `leaseViolation` for
 * the invariant and why the double-switch driver cannot trip it.
 *
 * This module value-imports **nothing** sheet-related (only `Tracing` +
 * `./ledger-render-skip`; `RowLocalId` is type-only), so it is TDZ-inert — no
 * variant-chain cycle risk. Minting lives in the `SourceRowCellData`
 * constructor (the probe IS the store), so nothing here imports the store back.
 */

import type { Option } from "../core/option.ts";
import { Tracing } from "@tento-core/tracing";

import { RenderSkip } from "./ledger-render-skip.ts";
import type { RenderFlags } from "./render-flags.ts";

import type { RowLocalId } from "../ids/row-local-id.ts";

/**
 * The per-sheet registry of render cursors. Reached via
 * `stores.sourceCellData.renderCursors`; `null` on ledger-less / standalone
 * sheets, where every consumer call `?.`-no-ops.
 */
export interface RenderCursors {
  /**
   * Borrow a paint surface, refcounted. The same `surfaceId` ⇒ the
   * SAME cursor and the same records (a second holder shares them);
   * `options.mode` is honored on **first mint** only, so a second acquire can
   * never flip a dark surface live.
   *
   * The holder MUST `release()` when it stops painting this registry. After the
   * LAST release the registry deletes the surface — cursor, `RenderSkip`
   * records, painted epochs — so a cached sheet's registry does not accumulate
   * the cursor of every binder that ever visited it, and a re-`acquire` of the
   * same id starts a fresh epoch rather than resuming stale records.
   *
   * Breaking that MUST on an exclusive id (`uniqueSurfaceId`) emits
   * `witness/render-cursor-leak` — see `leaseViolation`.
   */
  acquire(surfaceId: string, options: RenderCursors.CursorOptions): RenderSurfaceLease;
  /**
   * SDK-owned eviction (wired on `SheetStructureChanged`): bump the structural
   * epoch and drop every cursor's records for rows no longer in `liveRows`. The
   * epoch bump forces surviving rows to re-paint once (position/child-set may
   * have shifted with zero version movement).
   */
  reconcile(liveRows: Iterable<RowLocalId>): void;
  /**
   * Epoch-only bump (wired on `NewRowInserted`, O(1)): the inserted row orphans
   * no record, but siblings whose child-set/position-dependent output shifts
   * must flip once — the next `shouldRebind` on each does exactly that.
   *
   * The registry is per sheet, so this epoch is already per-sheet state; the
   * SCOPE is enforced by the caller, which drops any `NewRowInserted` /
   * `SheetStructureChanged` that does not `concern` its own sheet
   * (`raw-sheet.ts` hydrate wiring). Calling this for a foreign sheet's insert
   * is the cross-sheet epoch storm (sdk-0 C8).
   */
  invalidateStructure(): void;
  /** Pull-based aggregate — surfaces (cursor count), epoch, summed counters. */
  snapshot(): RenderCursors.Snapshot;
}

/**
 * A borrowed paint surface. `sheets-ui` holds exactly one per registry
 * it paints and releases it the moment it moves off that registry.
 *
 * `release()` is idempotent **per lease**, so a double teardown can never free a
 * surface a co-holder is still using; the registry's refcount is what decides,
 * and it never goes below zero. Nothing survives the final release: the next
 * `acquire` of the same id mints a fresh cursor with no records and zeroed
 * counters, which is why re-entry after a release repaints every row instead of
 * trusting a record the surface no longer owns.
 */
export interface RenderSurfaceLease {
  /**
   * The leased surface's cursor. Shared with every co-holder of the same
   * `surfaceId`; stale (records cleared, no longer registered) once the last
   * lease has released.
   */
  readonly cursor: RenderCursor;
  /**
   * Hand the surface back. Idempotent — a second call on the same lease is a
   * no-op. The registry deletes the surface and every row record on it once the
   * last lease releases.
   */
  release(): void;
}

/**
 * A single paint surface's cursor. The consumer contract: (1) only consult
 * `shouldRebind` when YOUR surface already holds this row's prior output (DOM:
 * the rid gate; CLI: a carried-cache hit); (2) `markPainted` after a FULL paint;
 * (3) `evict` after a partial paint.
 */
export interface RenderCursor {
  /**
   * The LR6 version compare + structural-epoch gate. `true` = paint. A dark
   * cursor (`mode: "dark"`) always returns `true`; the decision is still counted.
   * An epoch-forced rebind (the row's `paintedEpoch` lags the registry) returns
   * `true` WITHOUT counting an authorized skip.
   */
  shouldRebind(id: RowLocalId): boolean;
  /** Record `id` as fully painted at the current versions + epoch. */
  markPainted(id: RowLocalId): void;
  /** Drop `id`'s record (recycling / partial-bind reset — never skip next). */
  evict(id: RowLocalId): void;
  /** Drop every record + painted-epoch (the `clearRowModes` bulk reset). */
  reset(): void;
  // ── Dark-phase referee hooks (the DOM fingerprint protocol) ──────────────
  // 1:1 delegates to the composed per-surface `RenderSkip`; L5 semantics
  // untouched, so the DOM dark stream is byte-identical.
  /** Raw L5 decision (counts decisions + would-skips). */
  decide(id: RowLocalId): RenderSkip.Decision;
  /** Raw L5 skip authorization (counts flag-on skips). */
  shouldSkip(decision: RenderSkip.Decision): boolean;
  /** Raw L5 referee (loud on `wouldSkip && changedOutput`). */
  refereeAfterBind(decision: RenderSkip.Decision, changedOutput: boolean): void;
  /** This surface's pull-based counters. */
  snapshot(): RenderSkip.Counters;
}

/**
 * Types and values that go with `RenderCursors`: `CursorOptions`, `Snapshot`,
 * `make`, `uniqueSurfaceId`.
 */
export namespace RenderCursors {
  /** Options for `acquire(surfaceId, …)`. */
  export interface CursorOptions {
    /**
     * Whether the cursor may actually short-circuit a paint (the composed
     * `RenderSkip`'s `RenderSkip.Mode`). Honored on first mint only.
     *
     * `"follow-flags"` = no per-cursor opinion: follow the sheet's
     * `registries.render.live()`, re-read at every decision. That is what the DOM binder mints, so
     * a mid-session spotlight toggle reaches an already-minted cursor despite
     * first-mint-wins. `"live"` / `"dark"` pin the cursor for its lifetime.
     */
    readonly mode: RenderSkip.Mode;
  }

  /** Pull-based aggregate for `RenderCursors.snapshot`. */
  export interface Snapshot {
    /**
     * Number of live paint surfaces — those still leased. Returns to
     * its baseline once every holder releases, which is what the seam-17 gate
     * reads.
     */
    readonly surfaces: number;
    /** Current structural epoch. */
    readonly epoch: number;
    /** Counters summed across every cursor. */
    readonly totals: RenderSkip.Counters;
  }

  /**
   * Mint a registry over `probe` — implemented rows-side by
   * `SourceRowCellData` (a `RenderSkip.AvailabilityProbe`), which owns the
   * `LedgerBinding` key resolution so the compare node equals the bumped node.
   * Typed as the wider `RenderSkip.Probe` so a legacy
   * `currentVersions`-only probe still mints a registry. `flags` is the
   * sheet's `SheetRegistries.render`: a cursor minted with `mode: "follow-flags"`
   * follows its `live()`.
   */
  export function make(probe: RenderSkip.Probe, flags: RenderFlags): RenderCursors {
    return new RenderCursorsImpl(probe, flags);
  }

  /**
   * Per-binder-instance surfaces MUST use this — two binders sharing an id would
   * cross-skip each other's slots (records are per-slot truth). Returns
   * `"<prefix>#<n>"` with a process-monotonic `n`.
   */
  export function uniqueSurfaceId(prefix: string): string {
    surfaceCounter += 1;
    return `${prefix}#${surfaceCounter}`;
  }
}

/** Process-monotonic source for `uniqueSurfaceId`. Never persisted. */
let surfaceCounter = 0;

/**
 * The shape `uniqueSurfaceId` mints — a `"<prefix>#<n>"` tail. An id of this
 * shape is **exclusive by contract**: `uniqueSurfaceId` exists precisely so
 * that one holder owns one id ("two binders sharing an id would cross-skip each
 * other's slots"), so a second live lease on such an id is never legitimate
 * sharing. Ids that do NOT match — the CLI's stable `"cli-tui"`, a test's
 * `"s"` — are the documented co-holder case, where a refcount above one is
 * exactly what `acquire` promises.
 */
const EXCLUSIVE_SURFACE_ID = /#\d+$/;

/** Why a lease is a discipline violation. The `reason` leaf of the witness. */
type LeaseViolation = "reacquire-while-held";

/**
 * The seam-17 lease invariant, as a pure function of the id being acquired and
 * the refcount it already carries: **an exclusive surface id must never be
 * acquired while a lease on it is still outstanding.**
 *
 * This is the leak in the only form one registry can observe. The registry is
 * per sheet; a holder that leaves a sheet must `release()` before acquiring
 * elsewhere, and the last release DELETES the entry — so a holder returning to
 * a sheet it left correctly always mints a fresh entry with `leases === 0`.
 * Finding its own id still held means the departure did not release, which is
 * the surface (and the row records under it) that would now accumulate.
 *
 * It cannot false-positive on the double-switch driver. Each switch releases
 * the lease on the registry being left before acquiring on the one being
 * entered, so on any single registry that binder holds at most one lease and
 * every switch nets to zero held growth; a revisit therefore sees a deleted —
 * not a held — entry. Nor can two holders collide: `cursorSurfaceId` is minted
 * per binder instance from a process-monotonic counter, so N concurrent binders
 * painting one sheet are N distinct ids, each checked on its own. And the
 * legitimate co-holder patterns (the CLI's stable `"cli-tui"`, a suite sharing
 * one id deliberately) do not carry the exclusive shape at all.
 */
function leaseViolation(
  surfaceId: string,
  outstandingLeases: number,
): Option<LeaseViolation> {
  if (outstandingLeases < 1) return null;
  if (!EXCLUSIVE_SURFACE_ID.test(surfaceId)) return null;
  return "reacquire-while-held";
}

/**
 * One registered paint surface: its cursor plus what keeps it alive — the
 * number of unreleased leases.
 */
class SurfaceEntry {
  /** Unreleased `acquire` leases. Never negative — `release` floors at zero. */
  leases = 0;

  constructor(readonly cursor: RenderCursorImpl) {}
}

/** The handle `acquire` hands back. Idempotent per instance (see `released`). */
class SurfaceLease implements RenderSurfaceLease {
  #released = false;

  readonly #entry: SurfaceEntry;
  readonly #surfaceId: string;
  readonly #registry: RenderCursorsImpl;
  constructor(
    registry: RenderCursorsImpl,
    surfaceId: string,
    entry: SurfaceEntry,
  ) {
    this.#registry = registry;
    this.#surfaceId = surfaceId;
    this.#entry = entry;
  }

  get cursor(): RenderCursor {
    return this.#entry.cursor;
  }

  release(): void {
    // Per-lease idempotence is what keeps the refcount honest: a double teardown
    // must not decrement twice and free a surface a co-holder still paints.
    if (this.#released) return;
    this.#released = true;
    this.#registry.releaseSurface(this.#surfaceId, this.#entry);
  }
}

class RenderCursorsImpl implements RenderCursors {
  #structuralEpoch = 0;
  readonly #surfaces = new Map<string, SurfaceEntry>();
  /** Lifetime `acquire` count — the census numerator (`render-cursor/lease`). */
  #acquired = 0;
  /** Lifetime real releases (a lease's first `release()` only). */
  #released = 0;

  /** The flags a cursor minted with `mode: "follow-flags"` follows (`SheetRegistries.render`). */
  readonly #flags: RenderFlags;

  readonly #probe: RenderSkip.Probe;
  constructor(probe: RenderSkip.Probe, flags: RenderFlags) {
    this.#probe = probe;
    this.#flags = flags;
  }

  acquire(
    surfaceId: string,
    options: RenderCursors.CursorOptions,
  ): RenderSurfaceLease {
    const entry = this.#entryFor(surfaceId, options);
    // Read the refcount BEFORE this acquire joins it: the invariant is about
    // what was already outstanding, not about the lease being handed out now.
    const violation = leaseViolation(surfaceId, entry.leases);
    entry.leases += 1;
    this.#acquired += 1;
    if (violation != null) {
      // Seam 17's tripwire. Unthrottled on purpose: it fires once per offending
      // acquire, so its rate IS the leak rate — one line per sheet switch at
      // worst, and none at all while the discipline holds.
      Tracing.log("witness/render-cursor-leak", {
        surfaces: this.#surfaces.size,
        surfaceId,
        refcount: entry.leases,
        reason: violation,
      });
    }
    return new SurfaceLease(this, surfaceId, entry);
  }

  /**
   * Get or mint the surface for `surfaceId`. First-mint wins: re-entry
   * reattaches to the same records and `opts.mode` on a later call is ignored (a
   * byte-identical dark surface must never silently go live because a second
   * caller passed `mode: "live"`). A mint after a full release is a FIRST mint —
   * fresh cursor, fresh records, `mode` honored again.
   */
  #entryFor(
    surfaceId: string,
    opts: RenderCursors.CursorOptions,
  ): SurfaceEntry {
    const existing = this.#surfaces.get(surfaceId);
    if (existing != null) return existing;
    const created = new SurfaceEntry(
      new RenderCursorImpl(this, this.#probe, this.#flags, opts.mode),
    );
    this.#surfaces.set(surfaceId, created);
    return created;
  }

  /**
   * A lease's first `release()`. Drops the refcount and, once nothing holds the
   * surface any more, deletes it: the cursor leaves the registry and its records
   * are cleared, so a lease anyone still holds a reference to cannot resurrect
   * state a later `acquire` would then trust. Every such release leaves a
   * `render-cursor/lease` census breadcrumb carrying the post-release surface
   * count.
   */
  releaseSurface(surfaceId: string, entry: SurfaceEntry): void {
    if (entry.leases > 0) entry.leases -= 1;
    this.#released += 1;
    const dropped = entry.leases === 0;
    if (dropped) {
      if (this.#surfaces.get(surfaceId) === entry) {
        this.#surfaces.delete(surfaceId);
      }
      entry.cursor.reset();
    }
    // Census, not a tripwire: one line per real release, read AFTER the delete
    // so `surfaces` is the count a holder actually left behind. A trace can
    // then see the registry return to baseline (or fail to) without depending
    // on the witness having fired.
    Tracing.log("render-cursor/lease", {
      surfaces: this.#surfaces.size,
      acquired: this.#acquired,
      released: this.#released,
    });
  }

  reconcile(liveRows: Iterable<RowLocalId>): void {
    const live = new Set(liveRows);
    this.#structuralEpoch += 1;
    for (const entry of this.#surfaces.values()) entry.cursor.pruneDead(live);
  }

  invalidateStructure(): void {
    this.#structuralEpoch += 1;
  }

  /** Current structural epoch — read by cursors' `shouldRebind` gate. */
  epoch(): number {
    return this.#structuralEpoch;
  }

  snapshot(): RenderCursors.Snapshot {
    let decisions = 0;
    let wouldSkip = 0;
    let skipped = 0;
    let divergences = 0;
    for (const entry of this.#surfaces.values()) {
      const c = entry.cursor.snapshot();
      decisions += c.decisions;
      wouldSkip += c.wouldSkip;
      skipped += c.skipped;
      divergences += c.divergences;
    }
    return {
      surfaces: this.#surfaces.size,
      epoch: this.#structuralEpoch,
      totals: { decisions, wouldSkip, skipped, divergences },
    };
  }
}

class RenderCursorImpl implements RenderCursor {
  readonly #skip: RenderSkip;
  /**
   * The structural epoch at which each row was last fully painted. A row whose
   * entry lags `registry.epoch()` (or is absent) is force-rebound by
   * `shouldRebind` regardless of the version compare — the LI6 gate.
   */
  readonly #paintedEpoch = new Map<RowLocalId, number>();

  readonly #registry: RenderCursorsImpl;
  constructor(
    registry: RenderCursorsImpl,
    probe: RenderSkip.Probe,
    flags: RenderFlags,
    mode: RenderSkip.Mode,
  ) {
    this.#registry = registry;
    this.#skip = new RenderSkip(probe, flags, mode);
  }

  shouldRebind(id: RowLocalId): boolean {
    // Always compute + count the decision (the L5 dark contract), even when the
    // epoch gate will force the rebind below.
    const decision = this.#skip.decide(id);
    const paintedAt = this.#paintedEpoch.get(id);
    if (paintedAt === undefined || paintedAt !== this.#registry.epoch()) {
      // Epoch-forced rebind: return BEFORE `shouldSkip` so it never counts as an
      // authorized skip.
      return true;
    }
    return !this.#skip.shouldSkip(decision);
  }

  markPainted(id: RowLocalId): void {
    this.#skip.recordNow(id);
    this.#paintedEpoch.set(id, this.#registry.epoch());
  }

  evict(id: RowLocalId): void {
    this.#skip.evict(id);
    this.#paintedEpoch.delete(id);
  }

  reset(): void {
    this.#skip.clear();
    this.#paintedEpoch.clear();
  }

  decide(id: RowLocalId): RenderSkip.Decision {
    return this.#skip.decide(id);
  }

  shouldSkip(decision: RenderSkip.Decision): boolean {
    return this.#skip.shouldSkip(decision);
  }

  refereeAfterBind(decision: RenderSkip.Decision, changedOutput: boolean): void {
    this.#skip.refereeAfterBind(decision, changedOutput);
  }

  snapshot(): RenderSkip.Counters {
    return this.#skip.snapshot();
  }

  /**
   * Drop records for every row not in `live` (the `reconcile` sweep). Iterates
   * the cursor's own `paintedEpoch` keys — a superset of the composed
   * `RenderSkip`'s records — so both maps are pruned together.
   */
  pruneDead(live: Set<RowLocalId>): void {
    for (const id of [...this.#paintedEpoch.keys()]) {
      if (!live.has(id)) {
        this.#skip.evict(id);
        this.#paintedEpoch.delete(id);
      }
    }
  }
}
